import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup, Typography,
} from '@material-ui/core';
import { useCompany } from 'contexts/CompanyContext';
import { useEmployeeFunctions } from 'contexts/EmployeeContext';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import DefaultButton from 'components/DefaultButton';
import MaiaLogo from 'components/MaiaLogo';
import RegisterLayout from 'pages/RegisterLayout';
import routes from 'config/routes';
import sideImage from '_assets/register/team.svg';
import useStyles from './styles';
import * as Yup from 'yup';
import { useMemo } from 'react';

const getOptions = (classes) => ([
  {
    type: 'lonely',
    description: (
      <Typography>
        Je gère mon entreprise et <span className={classes.importantText}>je suis seul</span> à réaliser les prestations clients.
      </Typography>
    ),
  },
  {
    type: 'together',
    description: (
      <Typography>
        Je gère mon entreprise, <span className={classes.importantText}>j’ai une équipe</span> et je réalise les prestations clients.
      </Typography>
    ),
  },
  {
    type: 'manager',
    description: (
      <Typography>
        <span className={classes.importantText}>Je gère mon entreprise</span>, j’ai une équipe qui réalise les prestations clients.
      </Typography>
    ),
  },
  {
    type: 'employee',
    description: (
      <Typography>
        <span className={classes.importantText}>Je suis salarié</span> d'une entreprise et je gère les activités de celle-ci.
      </Typography>
    ),
  },
]);

const validationSchema = Yup.object({
  companyType: Yup.string()
    .oneOf(['lonely', 'together', 'manager', 'employee'], 'La valeur choisis est incorrecte')
    .required('Choississez une configuration'),
});

const RegisterConfig = () => {
  const classes = useStyles();
  const history = useHistory();

  const { currentCompany } = useCompany();
  const { autoCreateEmployee } = useEmployeeFunctions();

  const configOptions = useMemo(() => getOptions(classes), [classes]);

  const { errors, handleChange, handleSubmit, isSubmitting, touched } = useFormik({
    initialValues: { companyType: '' },
    validationSchema,
    onSubmit: async (values) => {
      if (currentCompany?.id === null) return;

      if (['manager', 'employee'].includes(values.companyType)) {
        history.push(routes.register.team.root);
        return;
      }

      await autoCreateEmployee();
      history.push(routes.home.root);
    },
  });

  function handleCancel() {
    if (isSubmitting) return;
    history.push(routes.home.root);
  }

  const SideComponent = () => (
    <Grid container direction='column' style={{ height: 'auto' }}>
      <Grid container item justifyContent='center'>
        <MaiaLogo />
      </Grid>

      <Grid container item justifyContent='center' style={{ marginTop: '8vh' }}>
        <img src={sideImage} alt='Identity' width={200} />
      </Grid>

      <Grid container item justifyContent='center' style={{ marginTop: '3vh' }}>
        <Typography className={classes.text} align='center' variant='h3'>
          Dites-en nous un peu plus sur votre entreprise.
        </Typography>
        <Typography className={classes.text} align='center' variant='h3' style={{ marginTop: '3vh' }}>
          Comment fonctionnez vous ?
        </Typography>
        <Typography className={classes.text} align='center' variant='h3' style={{ marginTop: '3vh' }}>
          Quel cas correspond le plus à votre mode de fonctionnement ?
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <RegisterLayout SideComponent={<SideComponent />}>
      <Grid container direction='column' style={{ height: 'auto' }}>
        <Grid className={classes.header} container item justifyContent='center'>
          <Grid className={classes.title} container item xs={12} md={9} justifyContent='center'>
            <Typography variant='h1'>Votre équipe</Typography>
          </Grid>

          <Grid className={classes.title} container item xs={12} md={9} justifyContent='center'>
            <Typography align='center' variant='h2'>
              Nous allons vous aider à mettre en ligne votre première activité !
            </Typography>
          </Grid>

          <Grid className={classes.title} container item xs={12} md={9}>
            <Typography className={classes.subtitle} color='primary'>
              Pour commencer, identifiez quelle situation correspond le plus à votre cas.
            </Typography>
            <Typography className={classes.subtitle} color='primary'>
              Cela va nous aider à paramètrer votre entreprise.
            </Typography>
          </Grid>
        </Grid>

        <Grid container item justifyContent='center' style={{ flexGrow: 1 }}>
          <form onSubmit={handleSubmit}>
            <Grid container justifyContent='center' style={{ height: '100%' }}>
              <Grid container item xs={12} alignItems='center' justifyContent='center' style={{ flexGrow: 1 }}>
                <FormControl component="fieldset" error={errors.companyType}>
                  <RadioGroup
                    aria-label="companyType"
                    error={errors.companyType}
                    name="companyType"
                    onChange={handleChange}
                  >
                    {configOptions.map(({ description, type }) => (
                      <Box key={type} className={classes.question}>
                        {description}
                        <FormControlLabel
                          className={classes.radio}
                          value={type}
                          control={<Radio color='primary' />}
                          inputProps={{ 'aria-label': 'secondary checkbox' }}
                          label=""
                        />
                      </Box>
                    ))}
                  </RadioGroup>
                  {touched.companyType && Boolean(errors.companyType) && (
                    <FormHelperText>{errors.companyType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid container item xs={12} alignItems='flex-end' justifyContent='center' style={{ marginBottom: '16px' }}>
                <DefaultButton cancel onClick={handleCancel}>
                  Annuler
                </DefaultButton>
                <DefaultButton
                  color='secondary'
                  disabled={isSubmitting}
                  type='submit'
                  variant='contained'
                >
                  {isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
                </DefaultButton>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </RegisterLayout>
  );
};

export default RegisterConfig;
